import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gameTypes } from '../../data/GameTypes';
import { getGameTypeColors } from '../../utils/gameTypeColors';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { fetchGameStats } from '../../services/gameAnalytics';
import { normalizeCampaignStatus } from '../../data/campaignStatus';
import { playsLabel, cardSubtitle } from '../../utils/displayUtils';

// Helper function to format the created date
function formatDate(date) {
  const options = { month: 'short', day: 'numeric' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

// Parse createdAt from Firestore Timestamp, serialized { seconds, nanoseconds }, or ISO string
function getCampaignCreatedDate(createdAt) {
  if (!createdAt) return null;
  if (typeof createdAt?.toDate === 'function') return createdAt.toDate();
  if (typeof createdAt?.seconds === 'number') return new Date(createdAt.seconds * 1000);
  const date = new Date(createdAt);
  return isNaN(date.getTime()) ? null : date;
}

function CampaignsCard({ campaign }) {
  const [playCount, setPlayCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadStats = async () => {
      const stats = await fetchGameStats(campaign.id);
      if (mounted) {
        setPlayCount(stats.totalPlays ?? 0);
        setIsLoading(false);
      }
    };

    loadStats();

    return () => { mounted = false; };
  }, [campaign.id]);

  const gameTypeData = gameTypes.find((type) => type.id === campaign.gameType);
  const colors = getGameTypeColors(campaign.gameType);
  const status = normalizeCampaignStatus(campaign.status);

  const date = getCampaignCreatedDate(campaign.createdAt);
  const dateStr = date ? formatDate(date) : null;

  return (
    <Link
      to={`/studio/campaign/${campaign.id}`}
      className="group block col-span-full sm:col-span-6 xl:col-span-4 gch-card gch-card-hover transition-all duration-200"
    >
      <div className="flex flex-col h-full p-4 sm:p-5">
        <header className="flex items-start justify-between gap-3 mb-2 sm:mb-3">
          <div
            className="p-2.5 rounded-[var(--admin-card-radius)] flex-shrink-0"
            style={{
              background: colors?.bg,
              border: `1px solid ${colors?.border}`
            }}
          >
            {gameTypeData?.icon && (
              <gameTypeData.icon className="w-5 h-5" style={{ color: colors?.icon || 'var(--admin-accent)' }} />
            )}
          </div>
          <span className="text-[10px] font-medium uppercase tracking-wide gch-text-muted pt-1">
            {status}
          </span>
        </header>

        <div className="grow">
          <h2 className="text-xl leading-snug font-semibold mb-0.5 tracking-tight gch-text-primary">
            {campaign.internalName || campaign.options?.title || 'Untitled Campaign'}
          </h2>
          {(() => {
            const sub = cardSubtitle({
              internalName: campaign.internalName,
              title: campaign.options?.title,
              gameTypeDisplayValue: gameTypeData?.name,
              dateLabel: dateStr,
            });
            return sub ? (
              <p className="text-xs font-medium gch-text-muted truncate">{sub}</p>
            ) : null;
          })()}
        </div>

        <footer className="mt-3 sm:mt-4 flex justify-between items-center">
          <span className="text-xs gch-text-muted">
            {isLoading ? (
              <span className="inline-block h-4 w-20 bg-white/10 rounded animate-pulse" />
            ) : (
              [dateStr, playsLabel(playCount)].filter(Boolean).join(' · ')
            )}
          </span>
          <span className="text-xs font-semibold gch-text-muted group-hover:gch-text-primary transition-colors inline-flex items-center gap-0.5">
            View
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </span>
        </footer>
      </div>
    </Link>
  );
}

export default CampaignsCard;
